import React, { useRef, useEffect, useMemo } from "react";
import * as d3 from "d3";

interface ChartData {
  title?: string;
  labels?: string[];
  datasets?: Array<{ values?: number[] }>;
  currency?: string;
  chart_type?: string;
}

interface SimpleBarChartProps {
  chart: ChartData;
  currency?: string;
}

const formatAxisValue = (num: number, currency?: string) => {
  if (!currency) return String(num);
  const absNum = Math.abs(num);
  if (absNum >= 1_000_000) return `${(absNum / 1_000_000).toFixed(1)}M`;
  if (absNum >= 1_000) return `${(absNum / 1_000).toFixed(1)}K`;
  return String(absNum);
};

const formatBarLabel = (num: number, currency?: string) => {
  if (!currency) return String(num);
  const suffix = Math.abs(num) >= 1_000_000 ? "M" : Math.abs(num) >= 1_000 ? "K" : "";
  const absNum = Math.abs(num);
  const shortNum = absNum >= 1_000_000 ? (absNum / 1_000_000).toFixed(1) : absNum >= 1_000 ? (absNum / 1_000).toFixed(1) : String(absNum);
  return num < 0 ? `-${shortNum}${suffix}` : `${shortNum}${suffix}`;
};

const formatHover = (num: number, currency?: string) => {
  if (!currency) return String(num);
  const suffix = Math.abs(num) >= 1_000_000 ? "M" : Math.abs(num) >= 1_000 ? "K" : "";
  const absNum = Math.abs(num);
  const shortNum = absNum >= 1_000_000 ? (absNum / 1_000_000).toFixed(1) : absNum >= 1_000 ? (absNum / 1_000).toFixed(1) : String(absNum);
  const numStr = num < 0 ? `-${shortNum}${suffix}` : `${shortNum}${suffix}`;
  return `${numStr} ${currency}`;
};

const truncateLabel = (str: string, maxLen: number) => {
  if (str.length <= maxLen) return str;
  return str.slice(0, maxLen - 2) + "…";
};

export function SimpleBarChart({ chart, currency }: SimpleBarChartProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const data = useMemo(() => {
    if (!chart?.labels) return [];
    return chart.labels.map((label: string, i: number) => ({
      name: label,
      value: chart.datasets?.[0]?.values?.[i] || 0,
    }));
  }, [chart]);

  const chartCurrency = currency || chart?.currency || "AED";
  const showValueLabels = useMemo(() => data.length <= 6, [data]);
  const chartHeight = Math.max(data.length * 40 + 120, 220);
  const maxValue = useMemo(() => Math.max(...data.map((d) => Math.abs(d.value))), [data]);

  useEffect(() => {
    if (!svgRef.current || !data.length) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const containerWidth = svgRef.current.parentElement?.clientWidth || 300;
    const width = Math.max(containerWidth - 20, 200);
    const height = chartHeight;
    const margin = { top: 25, right: 12, bottom: 45, left: 50 };
    const barWidth = width - margin.left - margin.right;
    const barHeight = height - margin.top - margin.bottom;

    const hasNegative = data.some(d => d.value < 0);
    
    const padding = data.length > 5 ? 0.15 : 0.25;
    const x = d3
      .scaleBand()
      .domain(data.map((d) => d.name))
      .range([0, barWidth])
      .padding(padding);

    const yMin = hasNegative ? Math.min(0, d3.min(data, d => d.value) || 0) : 0;
    const yMax = Math.max(0, d3.max(data, d => d.value) || 0);
    const y = d3.scaleLinear()
      .domain([yMin * 1.15, yMax * 1.15])
      .range([barHeight, 0]);

    const chartGroup = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const yTicks = y.ticks(4).filter(t => t !== 0);
    chartGroup
      .selectAll(".grid-line")
      .data(yTicks)
      .enter()
      .append("line")
      .attr("x1", 0)
      .attr("x2", barWidth)
      .attr("y1", d => y(d))
      .attr("y2", d => y(d))
      .attr("stroke", "#E5E7EB")
      .attr("stroke-dasharray", "3,3");

    chartGroup
      .selectAll(".y-tick")
      .data(yTicks)
      .enter()
      .append("text")
      .attr("x", -6)
      .attr("y", d => y(d))
      .attr("dy", "0.35em")
      .attr("text-anchor", "end")
      .attr("font-size", "9px")
      .attr("fill", "#6B7280")
      .text(d => formatAxisValue(d, chartCurrency));

    if (hasNegative) {
      chartGroup
        .append("line")
        .attr("x1", 0)
        .attr("x2", barWidth)
        .attr("y1", y(0))
        .attr("y2", y(0))
        .attr("stroke", "#9CA3AF")
        .attr("stroke-width", 1);
    }

    chartGroup
      .selectAll(".bar")
      .data(data)
      .enter()
      .append("rect")
      .attr("x", d => x(d.name) || 0)
      .attr("y", d => d.value >= 0 ? y(d.value) : y(0))
      .attr("width", x.bandwidth())
      .attr("height", d => Math.abs(y(d.value) - y(0)))
      .attr("fill", d => d.value >= 0 ? "#4AC08C" : "#E85D3A")
      .attr("rx", 3)
      .style("cursor", "pointer")
      .on("mouseenter", function (event, d) {
        d3.select(this).attr("opacity", "0.85");
        if (tooltipRef.current) {
          tooltipRef.current.style.opacity = "1";
          tooltipRef.current.innerHTML = `<strong>${d.name}</strong><br/>${formatHover(d.value, chartCurrency)}`;
          tooltipRef.current.style.left = `${event.offsetX + 15}px`;
          tooltipRef.current.style.top = `${event.offsetY - 10}px`;
        }
      })
      .on("mouseleave", function () {
        d3.select(this).attr("opacity", "1");
        if (tooltipRef.current) {
          tooltipRef.current.style.opacity = "0";
        }
      });

    if (showValueLabels) {
      chartGroup
        .selectAll(".value-label")
        .data(data)
        .enter()
        .append("text")
        .attr("x", d => (x(d.name) || 0) + x.bandwidth() / 2)
        .attr("y", d => d.value >= 0 ? y(d.value) - 5 : y(d.value) + 12)
        .attr("text-anchor", "middle")
        .attr("font-size", "8px")
        .attr("font-weight", "600")
        .attr("fill", "#374151")
        .text(d => formatBarLabel(d.value, chartCurrency));
    }

    const xAxis = chartGroup
      .append("g")
      .attr("transform", `translate(0,${barHeight})`)
      .call(d3.axisBottom(x).tickSize(0));

    xAxis.selectAll("text").attr("font-size", "9px").attr("fill", "#6B7280");
    xAxis.selectAll(".domain").remove();

    if (data.length > 5) {
      xAxis.selectAll("text").each(function () {
        const text = d3.select(this);
        text.text(truncateLabel(text.text(), 8));
      });
    }
  }, [data, chartCurrency, showValueLabels, chartHeight, maxValue]);

  return (
    <div className="ai-summary-panel__card" ref={containerRef}>
      <p className="ai-summary-panel__card-title">{chart?.title || ""}</p>
      <div style={{ position: "relative", overflow: "visible", height: chartHeight, width: "100%" }}>
        <svg ref={svgRef} width="100%" height={chartHeight} style={{ display: "block" }}></svg>
        <div
          ref={tooltipRef}
          style={{
            position: "absolute",
            backgroundColor: "white",
            padding: "8px 12px",
            borderRadius: "6px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
            fontSize: "11px",
            color: "#374151",
            opacity: 0,
            transition: "opacity 0.15s",
            pointerEvents: "none",
            zIndex: 10,
            border: "1px solid #E5E7EB",
            lineHeight: 1.4,
          }}
        />
      </div>
    </div>
  );
}

export default SimpleBarChart;